import { KERB_LANE, LANE_WIDTH } from '@accra/shared';
import { BoxGeometry, CylinderGeometry, Group, Mesh, MeshStandardMaterial, PlaneGeometry, type CanvasTexture } from 'three';
import { flatMaterial } from '../../render/materials/cache';
import { canvasTexture } from '../../render/materials/canvasTexture';
import { laneX } from '../../systems/lanes';
import { STATION_HALF, nextStation, stationAt, type StationPoint } from '../fuelStations';
import { nightGlow } from '../buildings/facadeAtlas';
import { lampMaterial } from '../props/streetProps';

const SHOW_AHEAD = 340;
const SHOW_BEHIND = 40;

function bayTexture(): CanvasTexture {
  return canvasTexture(128, 512, (ctx) => {
    ctx.fillStyle = 'rgba(20,110,70,0.55)';
    ctx.fillRect(0, 0, 128, 512);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 10;
    ctx.strokeRect(6, 6, 116, 500);
    ctx.save();
    ctx.translate(64, 256);
    ctx.rotate(-Math.PI / 2);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 46px Impact, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('FUEL', 0, 16);
    ctx.restore();
  });
}

const signs = new Map<string, CanvasTexture>();

/** The station's price board: its name and "FUEL" on a green panel with a Ghana stripe. */
function signTexture(name: string): CanvasTexture {
  let texture = signs.get(name);
  if (!texture) {
    texture = canvasTexture(512, 256, (ctx) => {
      ctx.fillStyle = '#0b6e4f';
      ctx.fillRect(0, 0, 512, 256);
      ctx.fillStyle = '#fcd116';
      ctx.fillRect(0, 196, 512, 22);
      ctx.fillStyle = '#ce1126';
      ctx.fillRect(0, 174, 512, 22);
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.font = 'bold 66px "Arial Black", Impact, sans-serif';
      ctx.fillText(name.toUpperCase(), 256, 90, 480);
      ctx.font = 'bold 54px Impact, sans-serif';
      ctx.fillStyle = '#fcd116';
      ctx.fillText('⛽ FUEL · PETROL', 256, 150, 480);
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 34px Arial, sans-serif';
      ctx.fillText('Open 24 hours', 256, 244);
    });
    signs.set(name, texture);
  }
  return texture;
}

function post(x: number, z: number, material: MeshStandardMaterial): Mesh {
  const mesh = new Mesh(new CylinderGeometry(0.16, 0.16, 4.6, 8), material);
  mesh.position.set(x, 2.3, z);
  mesh.castShadow = true;
  return mesh;
}

function pump(x: number, z: number): Group {
  const group = new Group();
  const body = new Mesh(new BoxGeometry(0.8, 1.5, 0.65), flatMaterial('#c8102e'));
  body.position.y = 0.75;
  const top = new Mesh(new BoxGeometry(0.9, 0.2, 0.75), flatMaterial('#f2f2ee'));
  top.position.y = 1.6;
  const screen = new Mesh(new BoxGeometry(0.5, 0.35, 0.05), flatMaterial('#1b1b1b'));
  screen.position.set(-0.41, 1.1, 0);
  screen.rotation.y = Math.PI / 2;
  group.add(body, top, screen);
  group.position.set(x, 0.2, z);
  group.traverse((o) => (o.castShadow = true));
  return group;
}

interface Slot {
  group: Group;
  signMaterial: MeshStandardMaterial;
  seq: number;
}

function buildStation(bayMaterial: MeshStandardMaterial): Slot {
  const group = new Group();
  const forecourt = new Mesh(new PlaneGeometry(11, 26), flatMaterial('#8a8a86'));
  forecourt.rotation.x = -Math.PI / 2;
  forecourt.position.set(10.6, 0.22, 0);
  forecourt.receiveShadow = true;
  const bay = new Mesh(new PlaneGeometry(LANE_WIDTH - 0.3, STATION_HALF * 2), bayMaterial);
  bay.rotation.x = -Math.PI / 2;
  bay.position.set(laneX(KERB_LANE), 0.025, 0);

  const postMaterial = flatMaterial('#cfcfcb');
  const roof = new Mesh(new BoxGeometry(8.4, 0.35, 14.5), flatMaterial('#f2f2ee'));
  roof.position.set(10, 4.75, 0);
  roof.castShadow = true;
  const band = new Mesh(new BoxGeometry(8.5, 0.45, 0.2), flatMaterial('#ce1126'));
  band.position.set(10, 4.75, 7.3);
  const underlight = new Mesh(new PlaneGeometry(6.6, 11), lampMaterial);
  underlight.rotation.x = Math.PI / 2;
  underlight.position.set(10, 4.55, 0);
  const kiosk = new Mesh(new BoxGeometry(3.6, 3, 6), flatMaterial('#e6dfcf'));
  kiosk.position.set(14.6, 1.7, -3);
  kiosk.castShadow = true;
  const door = new Mesh(new BoxGeometry(0.1, 2.1, 1.2), flatMaterial('#2b6cb0'));
  door.position.set(12.78, 1.25, -3);

  const signMaterial = new MeshStandardMaterial({ emissive: '#ffffff', emissiveIntensity: 0.3 });
  const sign = new Mesh(new BoxGeometry(4.4, 2.2, 0.2), flatMaterial('#2a2d34'));
  sign.position.set(7.6, 7.2, 12.6);
  const face = new Mesh(new PlaneGeometry(4.2, 2.1), signMaterial);
  face.position.set(7.6, 7.2, 12.72);
  const pole = new Mesh(new CylinderGeometry(0.12, 0.14, 6.3, 8), postMaterial);
  pole.position.set(7.6, 3.2, 12.6);

  group.add(forecourt, bay, roof, band, underlight, kiosk, door, sign, face, pole,
    post(6.1, 6.4, postMaterial), post(6.1, -6.4, postMaterial), post(13.9, 6.4, postMaterial), post(13.9, -6.4, postMaterial),
    pump(7.2, 3.2), pump(7.2, -3.2));
  group.visible = false;
  return { group, signMaterial, seq: -1 };
}

/** Petrol stations on the kerb side: a canopy over two pumps, a shop, a price board, and a marked bay in the kerb lane. */
export class FuelStationView {
  readonly group = new Group();
  private readonly slots: Slot[];

  constructor() {
    const bayMaterial = new MeshStandardMaterial({ map: bayTexture(), transparent: true, depthWrite: false });
    this.slots = [buildStation(bayMaterial), buildStation(bayMaterial)];
    for (const slot of this.slots) this.group.add(slot.group);
  }

  /** Shows the next two stations when they're in view, with signs that glow at night. */
  update(busS: number): void {
    const first = nextStation(busS);
    const stations: (StationPoint | null)[] = [first, first ? stationAt(first.seq + 1) : null];
    this.slots.forEach((slot, i) => {
      const station = stations[i] ?? null;
      const near = station !== null && station.position - busS < SHOW_AHEAD && busS - station.position < SHOW_BEHIND;
      slot.group.visible = near;
      if (!near || !station) return;
      if (slot.seq !== station.seq) {
        slot.seq = station.seq;
        const texture = signTexture(station.name);
        slot.signMaterial.map = texture;
        slot.signMaterial.emissiveMap = texture;
        slot.signMaterial.needsUpdate = true;
      }
      slot.group.position.set(0, 0, -station.position);
      slot.signMaterial.emissiveIntensity = 0.3 + 1.0 * nightGlow.value;
    });
  }
}
